import React from 'react';
import { object } from 'yup';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import styles from './styles.scss';
import withForm from '../../../../../../admin/hocs/withForm';
import Icon from '../../../../../../modules/react/components/stateless/Icon';
import Price from '../../../../../components/stateless/Price';
import Translation from '../../../../../components/stateless/Translation';
import LoadingIndicator from '../../../../../components/stateless/LoadingIndicator';
import SummaryLineItem from './SummaryLineItem';
import FacilityImage from '../../../../../containers/FacilityImage';
import {
  GBP,
} from '../../../../../../modules/i18n/currencies';

const propTypes = {
  facility: PropTypes.shape({
    id: PropTypes.string.isRequired,
    address: PropTypes.shape({
      streetAddress: PropTypes.string.isRequired,
      areaAddress: PropTypes.string.isRequired,
    }).isRequired,
    settings: PropTypes.shape({
      useWeeklyRates: PropTypes.bool.isRequired,
    }).isRequired,
  }),
  showMobileSummary: PropTypes.bool.isRequired,
  setShowMobileSummary: PropTypes.func.isRequired,
  receipt: PropTypes.shape({
    creditAmount: PropTypes.number.isRequired,
    feeAmount: PropTypes.number.isRequired,
    rentAmount: PropTypes.number.isRequired,
    rentMonth: PropTypes.string.isRequired,
    nextDueDate: PropTypes.number.isRequired,
    totalAmount: PropTypes.number.isRequired,
    discountPlans: PropTypes.array().isRequired,
  }),
  receiptLoading: PropTypes.bool.isRequired,
  availableUnit: PropTypes.shape(),
};

const defaultProps = {
  facility: undefined,
  receipt: undefined,
  availableUnit: undefined,
};

const validationSchema = object().shape({});

const initialValues = {};

export const MobileOrderSummary = ({ setShowMobileSummary, facility, availableUnit }) => {
  if (!facility || !facility.unitGroup) {
    return null;
  }

  const {
    unitGroup,
    settings: { useWeeklyRates },
    currency,
    currencyLocale,
    address: {
      streetAddress,
      areaAddress,
    },
  } = facility;

  const {
    price,
    discountedPrice,
    discountedPriceWeekly,
    priceWeekly,
  } = unitGroup;

  const weeklyRate = (discountedPriceWeekly || priceWeekly);
  const monthlyRate = (discountedPrice || price);
  const unitPrice = useWeeklyRates ? weeklyRate : monthlyRate;

  return (
    <>
      <div className={`${styles.orderSummaryMobile} sticky top-0 z-20 lg:hidden`}>
        <div className="shadow-md border border-gray-200 bg-white mb-4 p-4">
          <div className="flex gap-4 justify-between">
            <div>
              <div className="font-bold text-base leading-4">{streetAddress}</div>
              <div className="mb-2 text-base text-[#565958]">{areaAddress}</div>
            </div>
            <div>
                <button
                  type="button"
                  onClick={() => setShowMobileSummary(true)}
                  className="flex items-center text-underline leading-none p-0"
                >
                  <Translation
                    id="widgets.checkout.summary.title"
                    defaultMessage="View Details"
                  />
                  <Icon id="select-arrow" className="ml-1 rotate-90" />
                </button>
            </div>
          </div>
          <div>
            <span className="font-bold">
              <Translation
                id="widgets.checkout.summary.unit"
                defaultMessage="Unit:"
              />
            </span>
            {' '}
            {`${get(availableUnit, 'width')}'x${get(availableUnit, 'length')}'`}
            {' - '}
            {!useWeeklyRates ? (
              <>
                <Price
                  currency={currency}
                  locale={currencyLocale}
                  value={unitPrice}
                />
                <Translation id="widgets.abbreviation.mo" defaultMessage="/mo" />
              </>
            ) : (
              <>
                <Price
                  currency={currency}
                  locale={currencyLocale}
                  value={unitPrice}
                />
                <Translation id="widgets.abbreviation.wk" defaultMessage="/wk" />
                <span className="text-[#565958] ml-1">
                  (<Translation id="widgets.abbreviation.billedmonth" defaultMessage="Billed Monthly" />)
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const OrderSummaryDetails = ({
  receipt,
  facility,
}) => {
  const {
    currency,
    currencyLocale,
  } = facility;

  return (
    <>
      {receipt ? (
        <>
          {receipt.invoiceableItems.length ? (
            <>
              {receipt.invoiceableItems.map(item => (
                <SummaryLineItem
                  key={item.fullDescription}
                  lineItem={item}
                  currency={currency}
                  currencyLocale={currencyLocale}
                  discountPlans={receipt.discountPlans}
                />
              ))}
            </>
          ) : null}
          {currency !== GBP.code && (
            <div className="flex gap-2 place-content-between mb-2">
              <div>
                <Translation
                  id="widgets.checkout.summary.taxes"
                  defaultMessage="Taxes"
                />
              </div>
              <Price
                currency={currency}
                locale={currencyLocale}
                value={receipt.taxAmount || 0}
              />
            </div>
          )}
        </>
      ) : null}
    </>
  );
};

const OrderSummary = ({
  facility,
  showMobileSummary,
  setShowMobileSummary,
  receipt,
  receiptLoading,
  availableUnit,
}) => {
  if (!facility || !facility.unitGroup || !availableUnit) {
    return null;
  }

  const {
    currency,
    currencyLocale,
    unitGroup,
    settings: { useWeeklyRates },
  } = facility;

  const {
    price,
    discountedPrice,
    discountedPriceWeekly,
    priceWeekly,
  } = unitGroup;

  const weeklyRate = (discountedPriceWeekly || priceWeekly);
  const monthlyRate = (discountedPrice || price);
  const unitPrice = useWeeklyRates ? weeklyRate : monthlyRate;

  let totalPrice;
  let unitRateWeeklyValue;
  let unitRate;

  if (receipt) {
    unitRateWeeklyValue = receipt.rentAmount / 4.33;
  } else {
    unitRateWeeklyValue = unitPrice;
  }

  if (useWeeklyRates) {
    totalPrice = receipt ? receipt.totalAmount : (unitPrice * 4.33).toFixed(2);
    unitRate = receipt ? receipt.rentAmount : (unitPrice * 4.33).toFixed(2);
  } else {
    totalPrice = receipt ? receipt.totalAmount : unitPrice;
    unitRate = receipt ? receipt.rentAmount : unitPrice;
  }

  return (
    <>
      <div
        className={`${styles.orderSummary} 
    bg-[#fbfaf9] rounded-lg sticky top-24 mt-[32px] text-black lg:block ${showMobileSummary ? `${styles.mobile} rounded-none p-4` : ''}`}
      >
        <button type="button" className={`${styles.arrowIcon} w-8 h-8 flex items-center justify-center mb-2 p-0 lg:hidden`} onClick={() => setShowMobileSummary(false)}>
          <Icon id="times" />
        </button>
        <div className="shadow-md border border-gray-200 bg-white mb-4 p-4 flex gap-4 rounded-md">
          <div className="w-1/4 max-w-24 sm:w-24 shrink-0">
            <div className="w-full relative pb-[100%]">
              <FacilityImage
                className="w-full h-full object-cover absolute"
                facility={facility}
              />
            </div>
          </div>
          <div>
            <div className="font-bold text-base leading-4">{facility.address.streetAddress}</div>
            <div className="mb-2 text-base text-[#565958]">{facility.address.areaAddress}</div>
            <div className="flex flex-col gap-2 mb-2">
              <div>
                <div>
                  <span className="font-bold">
                    <Translation
                      id="widgets.checkout.summary.unit"
                      defaultMessage="Unit:"
                    />
                  </span>
                  {' '}
                  {`${get(availableUnit, 'width')}'x${get(availableUnit, 'length')}'`}
                </div>
              </div>
              <div>
                {!useWeeklyRates ? (
                  <>
                    <span className="font-bold">
                      <Translation
                        id="widgets.checkout.summary.unitPrice"
                        defaultMessage="Rent:"
                      />
                    </span>
                    {' '}
                    <Price
                      currency={currency}
                      locale={currencyLocale}
                      value={unitPrice}
                    />
                    <Translation id="widgets.abbreviation.mo" defaultMessage="/mo" />
                  </>
                ) : (
                  <>
                    <Price
                      currency={currency}
                      locale={currencyLocale}
                      value={unitPrice}
                    >
                      {formattedPrice => (
                        <span>
                          {`${formattedPrice} `}
                          <Translation
                            id="widgets.checkout.summary.perWeek"
                            defaultMessage="Per Week"
                          />
                        </span>
                      )}
                    </Price>
                    <div className="text-xs text-[#565958]">
                      <Translation id="widgets.abbreviation.billedmonth" defaultMessage="Billed Monthly" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="shadow-md border border-gray-200 bg-white rounded-md">
          <div className="border-b-1 border-gray-300 p-4">
            <h2 className={`${styles.updateTitle}`}>
              <Translation
                id="widgets.checkout.summary.title"
                defaultMessage="Order Summary"
              />
            </h2>
          </div>
          <div className="p-4">
            {!receipt && !receiptLoading && (
              <>
                <div className="flex gap-2 place-content-between mb-2">
                  <div>
                    <>
                      {!useWeeklyRates ? (
                        <Translation
                          id="widgets.checkout.summary.normalRent"
                          defaultMessage="Monthly Rent"
                        />
                      ) : (
                        <>
                          <Price
                            currency={currency}
                            locale={currencyLocale}
                            value={unitRateWeeklyValue}
                          >
                            {formattedPrice => (
                              <span>
                                {`${formattedPrice} `}
                                <Translation
                                  id="widgets.checkout.summary.perWeek"
                                  defaultMessage="Per Week"
                                />
                              </span>
                            )}
                          </Price>
                          <div className="text-xs text-[#565958]">
                            <Translation id="widgets.abbreviation.billedmonth" defaultMessage="Billed Monthly" />
                          </div>
                        </>
                      )}
                    </>
                  </div>
                  <Price
                    currency={currency}
                    locale={currencyLocale}
                    value={unitRate}
                  />
                </div>
                <div className="text-[#565958] mt-2 mb-2 flex gap-2">
                  <i className="fa fa-fw fa-info-circle pt-[3px]" aria-hidden="true" />
                  <div>
                    <Translation
                      id="widgets.checkout.summary.prorated"
                      defaultMessage="Your first month will be pro-rated based on your check-in date."
                    />
                  </div>
                </div>
              </>
            )}
            {receiptLoading ? (
              <div
                className="mb-4 max-w-full h-36"
              >
                <LoadingIndicator />
              </div>
            ) : (
              <>
                <OrderSummaryDetails
                  receipt={receipt}
                  facility={facility}
                />
                <div className="border-t-1 mt-4 pt-4 border-gray-300">
                  <div className="flex gap-2 place-content-between">
                    <div className="font-bold">
                      <Translation
                        id="widgets.checkout.summary.firsttotaldue"
                        defaultMessage="1st Month Total"
                      />
                    </div>
                    <Price
                      currency={currency}
                      locale={currencyLocale}
                      value={totalPrice}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {showMobileSummary && (
        <button aria-label="close overlay Summary" type="button" className="bg-black fixed inset-0 opacity-0 z-50 opacity-50" onClick={() => setShowMobileSummary(false)} />
      )}
    </>
  );
};

OrderSummary.propTypes = propTypes;
OrderSummary.defaultProps = defaultProps;

export default withForm({
  mapPropsToValues: () => (
    initialValues
  ),
  validationSchema,
  handleSubmit: (formPayload, formikBag) => formikBag.props.onSubmit(formPayload),
})(OrderSummary);
